import type { APIDeckPermissions } from '~~/entities/api'
import { DeckBaseAccess, type DeckPermissionsUserRole } from '~~/entities/deck'
import { Role } from '~~/entities/user'
import type { SecureLink } from '~~/entities/secure-link'

export function useDeckPermissions() {
  const { state: userState, hasRole } = useUser()

  const state = useState<{ deckId: number | null; companyId: number | null; permissions: APIDeckPermissions | null }>(
    'deckPermissions',
    () => ({
      deckId: null,
      companyId: null,
      permissions: null,
    }),
  )

  function setState({ deckId, companyId }: { deckId: number; companyId: number }) {
    state.value = { ...state.value, deckId, companyId }
  }

  async function getDeckPermissions() {
    if (!state.value.companyId || !state.value.deckId) {
      return {}
    }
    try {
      const response = await $fetch<APIDeckPermissions>(`/v2/companies/${state.value.companyId}/decks/${state.value.deckId}/share`, {
        ...useFetchSharedOptions(),
      })
      state.value.permissions = response
      return response
    } catch {
      return {}
    }
  }

  function updateDeckPermissions({ baseAccess }: { baseAccess: string }) {
    return $fetch<APIDeckPermissions>(`/v2/companies/${state.value.companyId}/decks/${state.value.deckId}/share-base`, {
      ...useFetchSharedOptions(),
      method: 'PATCH',
      body: {
        base_access: baseAccess,
        id: state.value.deckId,
      },
    })
  }

  function upsertDeckUserPermissions({ user_id, role }: { user_id: number; role: DeckPermissionsUserRole }) {
    return $fetch<APIDeckPermissions>(`/v2/companies/${state.value.companyId}/decks/${state.value.deckId}/share`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: {
        user_id,
        role,
      },
    })
  }

  function deleteDeckUserPermissions({ user_id }: { user_id: number }) {
    return $fetch<APIDeckPermissions>(`/v2/companies/${state.value.companyId}/decks/${state.value.deckId}/share`, {
      ...useFetchSharedOptions(),
      method: 'DELETE',
      body: {
        user_id,
      },
    })
  }

  function createSecureLink(body: { deck_id: number; email: string }) {
    return $fetch<SecureLink>(`/v2/companies/${state.value.companyId}/decks/secure_link`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body,
    })
  }

  function resendSecureLink(id: number) {
    return $fetch<SecureLink>(`/v2/decks/secure_link/${id}/resend`, {
      ...useFetchSharedOptions(),
      method: 'POST',
    })
  }

  function deleteSecureLink(id: number) {
    return $fetch(`/v2/decks/secure_link/${id}`, {
      ...useFetchSharedOptions(),
      method: 'DELETE',
    })
  }

  function requestRenewalByUUID(uuid: string) {
    return $fetch(`/v2/decks/secure_link/renew`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: {
        uuid,
      },
    })
  }

  function userHasPermission() {
    if (!state.value.permissions) return false

    if (hasRole(Role.InternalAdmin) || deckHasBaseAccess(DeckBaseAccess.Public)) {
      return true
    } else if (deckHasBaseAccess(DeckBaseAccess.Private)) {
      return state.value.permissions.roles.some(role => role.user_id === userState.value.user?.id)
    } else if (deckHasBaseAccess(DeckBaseAccess.CompanyView) || deckHasBaseAccess(DeckBaseAccess.CompanyEdit)) {
      return userState.value.user?.companies.some(company => company.id === state.value.companyId)
    }

    return false
  }

  function deckHasBaseAccess(baseAccess: DeckBaseAccess) {
    return state.value.permissions?.base_access === baseAccess
  }

  return {
    getDeckPermissions,
    updateDeckPermissions,
    upsertDeckUserPermissions,
    deleteDeckUserPermissions,
    userHasPermission,
    setDeckPermissionsState: setState,
    deckPermissionsState: state,
    deckHasBaseAccess,
    createSecureLink,
    resendSecureLink,
    deleteSecureLink,
    requestRenewalByUUID,
  }
}
